import React, { useState, useMemo, useEffect } from 'react';
import EcommerceScrollToTop from '../ecommerce/EcommerceScrollToTop';
import { getImageCandidateUrls } from '../../utils/imageHelper';

const UnifiedFooter = ({
  storeConfig,
  landingConfig
}) => {
  const footerConfig = landingConfig?.footer || storeConfig?.footer || {};
  const companyTitle = footerConfig.companyName || landingConfig?.logoText || storeConfig?.logoText || '';
  const showLogoText = landingConfig?.showLogoText !== false && storeConfig?.showLogoText !== false;
  const rawLogoUrl = footerConfig.logoUrl || landingConfig?.logoUrl || storeConfig?.logoUrl || '';
  const logoCandidates = useMemo(() => getImageCandidateUrls(rawLogoUrl), [rawLogoUrl]);
  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoCandidateIndex(0);
    setLogoFailed(false);
  }, [rawLogoUrl]);

  const tagline = landingConfig?.tagline !== undefined
    ? landingConfig.tagline
    : (footerConfig.tagline !== undefined ? footerConfig.tagline : '');
  const logoSubtitle = landingConfig?.logoSubtitle !== undefined
    ? landingConfig.logoSubtitle
    : (storeConfig?.logoSubtitle !== undefined ? storeConfig.logoSubtitle : 'FÁBRICA & TIENDA OFICIAL');
  const showLogoSubtitle = landingConfig?.showLogoSubtitle !== false && storeConfig?.showLogoSubtitle !== false && Boolean(logoSubtitle && String(logoSubtitle).trim());
  const phone = landingConfig?.contactSection?.phone || landingConfig?.topBar?.phone || footerConfig.phone || '(011) 4247-6622';
  const whatsapp = landingConfig?.contactSection?.whatsapp || landingConfig?.topBar?.whatsapp || footerConfig.whatsapp || '5491142476622';
  const email = landingConfig?.contactSection?.email || landingConfig?.topBar?.email || footerConfig.email || 'ventas@hiperpel.com.ar';
  const address = landingConfig?.contactSection?.address || footerConfig.address || 'Vicente Damonte 1237 / 1247, Lanús Este (B1824ALG), Buenos Aires';
  const scheduleMonFri = landingConfig?.contactSection?.scheduleMonFri || footerConfig.scheduleMonFri || '08:00 a 18:00 hs';
  const scheduleSat = landingConfig?.contactSection?.scheduleSat || footerConfig.scheduleSat || '08:00 a 13:00 hs';
  const copyright = footerConfig.copyrightText || `© ${new Date().getFullYear()} ${companyTitle || 'SISGECOM'} - Todos los derechos reservados.`;

  // Configuración de redes sociales oficiales
  const showSocials = footerConfig.showSocials !== false;
  const socialLinksConfig = footerConfig.socialLinks || {};

  const getSocialInfo = (key) => {
    const directVal = socialLinksConfig[key] !== undefined ? socialLinksConfig[key] : footerConfig[key];
    if (!directVal) return { enabled: false, url: '' };
    if (typeof directVal === 'object') {
      const isEnabled = directVal.enabled !== false && Boolean(directVal.url && String(directVal.url).trim());
      return { enabled: isEnabled, url: String(directVal.url || '').trim() };
    }
    if (typeof directVal === 'string') {
      const trimmed = directVal.trim();
      return { enabled: Boolean(trimmed), url: trimmed };
    }
    return { enabled: false, url: '' };
  };

  const socialNetworks = [
    {
      key: 'instagram',
      name: 'Instagram',
      icon: 'bi-instagram',
      style: { background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', color: '#ffffff' },
      getUrl: (val) => val.startsWith('http') ? val : `https://instagram.com/${val.replace('@', '')}`
    },
    {
      key: 'facebook',
      name: 'Facebook',
      icon: 'bi-facebook',
      style: { backgroundColor: '#1877f2', color: '#ffffff' },
      getUrl: (val) => val.startsWith('http') ? val : `https://facebook.com/${val}`
    },
    {
      key: 'x',
      name: 'X (Twitter)',
      icon: 'bi-twitter-x',
      style: { backgroundColor: '#000000', color: '#ffffff' },
      getUrl: (val) => val.startsWith('http') ? val : `https://x.com/${val.replace('@', '')}`
    },
    {
      key: 'linkedin',
      name: 'LinkedIn',
      icon: 'bi-linkedin',
      style: { backgroundColor: '#0a66c2', color: '#ffffff' },
      getUrl: (val) => val.startsWith('http') ? val : (val.includes('linkedin.com') ? `https://${val}` : `https://linkedin.com/company/${val.replace('@', '')}`)
    },
    {
      key: 'tiktok',
      name: 'TikTok',
      icon: 'bi-tiktok',
      style: { backgroundColor: '#000000', color: '#ffffff' },
      getUrl: (val) => val.startsWith('http') ? val : `https://tiktok.com/@${val.replace('@', '')}`
    }
  ];

  const activeSocials = socialNetworks
    .map(net => {
      let info = getSocialInfo(net.key);
      if (!info.enabled && net.key === 'x') {
        info = getSocialInfo('twitter');
      }
      return {
        ...net,
        enabled: info.enabled,
        url: info.enabled ? net.getUrl(info.url) : ''
      };
    })
    .filter(net => net.enabled && net.url);

  return (
    <>
      <footer 
        className="ecom-footer border-top mt-5 pt-4 pb-3"
        style={{
          backgroundColor: 'var(--ecom-footer-bg, #ffffff)',
          color: 'var(--ecom-footer-text, #64748b)',
          borderColor: 'var(--ecom-footer-border, #e2e8f0)',
          overflowX: 'hidden',
          maxWidth: '100vw'
        }}
      >
        <div className="container-fluid px-3 px-lg-4">
          <div className="row g-4 justify-content-between">
            
            {/* Columna 1: Identidad y Misión */}
            <div className="col-12 col-lg-4">
              <div className="d-flex align-items-center mb-3" style={{ gap: '14px' }}>
                {logoCandidates.length > 0 && !logoFailed ? (
                  <img 
                    src={logoCandidates[logoCandidateIndex]} 
                    alt={companyTitle || 'Logo'} 
                    style={{ height: '38px', width: 'auto', maxHeight: '38px', maxWidth: '140px', objectFit: 'contain', flexShrink: 0 }}
                    onError={() => {
                      if (logoCandidateIndex + 1 < logoCandidates.length) {
                        setLogoCandidateIndex(prev => prev + 1);
                      } else {
                        setLogoFailed(true);
                      }
                    }}
                  />
                ) : (
                  <div 
                    className="bg-primary text-white rounded-2 d-flex align-items-center justify-content-center fw-bold shadow-xs flex-shrink-0" 
                    style={{ width: '38px', height: '38px', fontSize: '1.1rem' }}
                  >
                    {companyTitle ? companyTitle.charAt(0).toUpperCase() : <i className="bi bi-building"></i>}
                  </div>
                )}
                {(showLogoText || showLogoSubtitle) && (
                  <div className="d-flex flex-column lh-1">
                    {showLogoText && (
                      <span className="fw-black fs-5 text-primary text-nowrap" style={{ fontWeight: 800, letterSpacing: '-0.3px' }}>
                        {companyTitle || 'SISGECOM'}
                      </span>
                    )}
                    {showLogoSubtitle && (
                      <span className="text-muted fw-semibold text-nowrap mt-0.5" style={{ fontSize: '0.65rem', letterSpacing: '0.8px' }}>
                        {logoSubtitle}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {tagline && (
                <p className="small text-muted mb-3" style={{ lineHeight: '1.5', maxWidth: '360px' }}>
                  {tagline}
                </p>
              )}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {storeConfig?.footer?.websiteUrl && (
                  <a 
                    href={storeConfig.footer.websiteUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-outline-primary btn-sm rounded-pill px-3" 
                    style={{ fontSize: '0.75rem' }}
                  >
                    <i className="bi bi-globe me-1"></i> {storeConfig.footer.websiteUrl.replace(/^https?:\/\//, '')}
                  </a>
                )}
                <a 
                  href="/login" 
                  className="btn btn-outline-secondary btn-sm rounded-pill px-3" 
                  style={{ fontSize: '0.75rem' }}
                >
                  <i className="bi bi-gear-fill me-1"></i> Sistema SISGECOM
                </a>
              </div>

              {/* Redes Sociales Oficiales */}
              {showSocials && activeSocials.length > 0 && (
                <div className="d-flex align-items-center gap-2 mt-3 pt-1 flex-wrap">
                  <span className="small fw-semibold me-1" style={{ fontSize: '0.78rem', color: 'var(--ecom-footer-text, #64748b)' }}>Redes:</span>
                  {activeSocials.map((net) => (
                    <a 
                      key={net.key}
                      href={net.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-sm rounded-circle p-0 d-inline-flex align-items-center justify-content-center shadow-xs" 
                      style={{ width: '32px', height: '32px', fontSize: '0.9rem', ...net.style }}
                      title={net.name}
                    >
                      <i className={`bi ${net.icon}`}></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

          {/* Columna 2: Contacto y Casa Central */}
          <div className="col-12 col-sm-6 col-md-4">
            <h6 className="fw-bold mb-2 text-uppercase small" style={{ letterSpacing: '0.5px', color: 'var(--ecom-footer-title, #1e293b)' }}>
              <i className="bi bi-telephone-fill text-primary me-2"></i> Atención y Ventas
            </h6>
            <ul className="list-unstyled small mb-0 lh-lg" style={{ color: 'var(--ecom-footer-text, #64748b)' }}>
              {phone && (
                <li>
                  <i className="bi bi-telephone text-primary me-2"></i>
                  <strong>Teléfono:</strong> {phone}
                </li>
              )}
              {whatsapp && (
                <li>
                  <i className="bi bi-whatsapp text-success me-2"></i>
                  <strong>WhatsApp Ventas:</strong> +{whatsapp.replace(/\D/g, '')}
                </li>
              )}
              {email && (
                <li>
                  <i className="bi bi-envelope text-primary me-2"></i>
                  <strong>Email:</strong> {email}
                </li>
              )}
              {address && (
                <li className="mt-1">
                  <i className="bi bi-geo-alt-fill text-danger me-2"></i>
                  <strong>Casa Central:</strong> {address}
                </li>
              )}
            </ul>
          </div>

          {/* Columna 3: Horarios de Atención */}
          <div className="col-12 col-sm-6 col-md-3">
            <h6 className="fw-bold mb-2 text-uppercase small" style={{ letterSpacing: '0.5px', color: 'var(--ecom-footer-title, #1e293b)' }}>
              <i className="bi bi-clock-fill text-primary me-2"></i> Horarios de Atención
            </h6>
            <ul className="list-unstyled small mb-0 lh-base" style={{ color: 'var(--ecom-footer-text, #64748b)' }}>
              <li className="mb-2">
                <strong>Lunes a Viernes:</strong><br />
                <span style={{ opacity: 0.9 }}>{scheduleMonFri}</span>
              </li>
              <li className="mb-0">
                <strong>Sábados:</strong><br />
                <span style={{ opacity: 0.9 }}>{scheduleSat}</span>
              </li>
            </ul>
          </div>
        </div>

        <div 
          className="d-flex flex-wrap align-items-center justify-content-between pt-3 mt-4 border-top small gap-2"
          style={{ 
            borderColor: 'var(--ecom-footer-border, #e2e8f0)',
            color: 'var(--ecom-footer-text, #64748b)',
            opacity: 0.85
          }}
        >
          <div>
            {copyright}
          </div>
          <div>
            Plataforma de catálogo online integrada con <strong>SIGECOM</strong>
          </div>
        </div>
      </div>
    </footer>

      {/* Botón flotante Volver Arriba para todas las páginas del sitio */}
      <EcommerceScrollToTop threshold={280} hasWhatsAppBelow={Boolean(whatsapp)} />
    </>
  );
};

export default UnifiedFooter;
